const { startCase } = require("lodash");
const { Joi, options } = require("../joi");
const { LANGUAGE } = require("../constants");

// isolated dictionary - access only from that place
let labels = LANGUAGE.labels;

exports.setLabels = messages => {
  labels = messages;
};

exports.withLabels = (definition, prefix = "") => {
  return Object.keys(definition).reduce((acc, key) => {
    const namespace = prefix ? `${prefix}.${key}` : key;
    acc[key] = labels.hasOwnProperty(namespace)
      ? definition[key].label(labels[namespace])
      : definition[key];
    return acc;
  }, {});
};

exports.objectWithLabels = (definition, prefix) =>
  Joi.object()
    .keys(exports.withLabels(definition, prefix))
    .options(options);

const joiSchemaOptions = [
  "abortEarly",
  "convert",
  "allowUnknown",
  "skipFunctions",
  "stripUnknown",
  "language",
  "presence",
  "raw",
  "context",
  "strip",
  "noDefaults",
  "escapeHtml",
];

const getJoiOptions = (options = {}) =>
  joiSchemaOptions.reduce((joiOptions, optionsName) => {
    if (options.hasOwnProperty(optionsName)) {
      joiOptions[optionsName] = options[optionsName];
    }
    return joiOptions;
  }, {});

exports.createWithOptions = definitionFn => (prefix, options = {}) =>
  definitionFn(prefix, options).options(getJoiOptions(options));

exports.createJoiObjectWithLabels = definitionFn => (prefix, options) => {
  const fields = definitionFn(prefix, options);

  return exports.objectWithLabels(fields, prefix, options);
};

exports.resolveLabel = ({ context }) => context.label || startCase(context.key);

exports.composeDecorators = (decorator, ...decorators) => {
  if (!decorators.length) {
    return decorator;
  }
  return decorator(exports.composeDecorators(...decorators));
};

exports.createModel = fn =>
  exports.composeDecorators(
    exports.createWithOptions,
    exports.createJoiObjectWithLabels,
    fn
  );

exports.createCollection = fn =>
  exports.composeDecorators(exports.createWithOptions, fn);

/**
 * Method to beautify global joi error which is similar to joi.prototype.messages from new joi version
 * @param config - enum of error type to message.
 * Message can be string or function. In order of function, error context is passed to prepare complex message
 * @returns {function(*): *}
 * @example
 Joi.string()
 .min(3)
 .max(15)
 .required()
 .error(
 exports.messages({
          "date.isoDate": error => M.$_IS_INVALID(resolveLabel(error)),
          "string.max": error => M.$_MAXIMUM_LENGTH_EXCEEDED(resolveLabel(error)),
          "string.base": error => M.$_HAS_INVALID_TYPE(resolveLabel(error)),
          "number.base": error => M.$_HAS_INVALID_TYPE(resolveLabel(error)),
        })
 );
 */
exports.messages = config => errors =>
  errors.map(error => {
    if (config.hasOwnProperty(error.type)) {
      error.message =
        typeof config[error.type] === "function"
          ? config[error.type](error)
          : config[error.type];
    }
    return error;
  });

exports.messages.resolveLabel = exports.resolveLabel;

/*
 * Convert options suitable for joi
 * @example from fieldAbortEarly to abortEarly
 */
exports.getFieldOptions = (options, defaultOptions = {}) => {
  options = options || {};

  const prefixFieldName = "field";
  const joiOptions = getJoiOptions(options);

  return Object.keys(options).reduce(
    (obj, key) => {
      const startIIndex = key.indexOf(prefixFieldName);

      if (startIIndex !== -1) {
        const pos = startIIndex + prefixFieldName.length;
        const newName =
          key.slice(pos, pos + 1).toLowerCase() + key.slice(pos + 1);

        obj[newName] = options[key];
      }

      return obj;
    },
    { ...defaultOptions, ...joiOptions }
  );
};
